import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  CornerDownRight,
  Link2,
  ListTodo,
  Plus,
  Unlink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useCreateProject,
  useCreateTask,
  useProjectSections,
  useProjects,
  useTasks,
  useUpdateTask,
  type TaskWithAssignees,
} from '@/features/projects/hooks';
import {
  DUE_TONE,
  PRIORITY_META,
  completionPercent,
  dueState,
  formatDueShort,
  statusOptionMeta,
  taskStatusOption,
} from '@/features/projects/display';
import { useUpdateEngProject, type EngProject } from '@/features/engineering/hooks';
import { formatError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { AvatarStack } from './EngParts';

interface EngTasksTabProps {
  project: EngProject;
  canEdit: boolean;
  canManage: boolean;
}

/**
 * The project's task list, which lives in the Tasks section. The tab
 * either helps you link/create that task list or shows its tasks
 * grouped by section with quick-complete and quick-add; anything deeper
 * (subtasks, comments, attachments) opens the task in Tasks.
 */
export function EngTasksTab({ project, canEdit, canManage }: EngTasksTabProps) {
  if (!project.pm_project_id) {
    return <NoTaskList project={project} canEdit={canEdit} />;
  }
  if (!project.pm_project) {
    return <InaccessibleTaskList project={project} canManage={canManage} />;
  }
  return <TaskList project={project} pmProjectId={project.pm_project_id} canEdit={canEdit} canManage={canManage} />;
}

// ---------------------------------------------------------------------------
// Empty / broken states
// ---------------------------------------------------------------------------

function NoTaskList({ project, canEdit }: { project: EngProject; canEdit: boolean }) {
  const { data: pmProjects } = useProjects({ includeArchived: false });
  const createPm = useCreateProject();
  const update = useUpdateEngProject();
  const [choice, setChoice] = useState<string>('');

  const teamIds = useMemo(() => {
    const ids = new Set<string>(project.eng_project_members.map((m) => m.profile_id));
    if (project.owner_id) ids.add(project.owner_id);
    return Array.from(ids);
  }, [project.eng_project_members, project.owner_id]);

  const link = async () => {
    if (!choice) return;
    try {
      await update.mutateAsync({ id: project.id, patch: { pm_project_id: choice } });
      toast.success('Task list linked.');
    } catch (err) {
      toast.error(formatError(err, 'Could not link the project.'));
    }
  };

  const create = async () => {
    try {
      const pm = await createPm.mutateAsync({
        name: `${project.code} · ${project.name}`,
        memberProfileIds: teamIds,
        department: 'engineering',
      });
      await update.mutateAsync({ id: project.id, patch: { pm_project_id: pm.id } });
      toast.success('Task list created in Tasks.');
    } catch (err) {
      toast.error(formatError(err, 'Could not create the task list.'));
    }
  };

  const busy = createPm.isPending || update.isPending;

  if (!canEdit) {
    return <EmptyState icon={ListTodo} title="This project has no task list yet" />;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4 text-info" />
            Create a task list
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Creates a task list in Tasks named <span className="font-medium text-foreground">{project.code} · {project.name}</span>{' '}
            tagged to the Engineering department, with the engineering team as members, and links it here.
          </p>
          <Button onClick={create} disabled={busy}>
            {busy ? 'Working…' : 'Create task list'}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Link2 className="h-4 w-4 text-info" />
            Link an existing project
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Task list</Label>
            <Select value={choice} onValueChange={setChoice}>
              <SelectTrigger aria-label="Task list">
                <SelectValue placeholder="Choose a task list" />
              </SelectTrigger>
              <SelectContent>
                {(pmProjects ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={link} disabled={!choice || busy}>
            Link task list
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function InaccessibleTaskList({ project, canManage }: { project: EngProject; canManage: boolean }) {
  const update = useUpdateEngProject();
  const unlink = async () => {
    if (!window.confirm('Unlink the task list from this engineering project? The task list itself is not deleted.')) return;
    try {
      await update.mutateAsync({ id: project.id, patch: { pm_project_id: null } });
      toast.success('Task list unlinked.');
    } catch (err) {
      toast.error(formatError(err, 'Could not unlink the task list.'));
    }
  };
  return (
    <EmptyState
      icon={ListTodo}
      title="The linked task list isn’t available to you"
      detail="Either you’re not a member of that task list or it has been deleted. Ask the task list owner to add you, or relink a different task list."
      action={
        canManage ? (
          <Button variant="outline" onClick={unlink} disabled={update.isPending}>
            <Unlink className="mr-2 h-4 w-4" />
            Unlink task list
          </Button>
        ) : undefined
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Task list
// ---------------------------------------------------------------------------

interface TaskGroup {
  key: string;
  label: string | null;
  tasks: Array<{ task: TaskWithAssignees; subtasks: TaskWithAssignees[] }>;
}

function TaskList({
  project,
  pmProjectId,
  canEdit,
  canManage,
}: {
  project: EngProject;
  pmProjectId: string;
  canEdit: boolean;
  canManage: boolean;
}) {
  const { data: tasks, isLoading } = useTasks(pmProjectId);
  const { data: sections } = useProjectSections(pmProjectId);
  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const updateProject = useUpdateEngProject();
  const [showCompleted, setShowCompleted] = useState(false);
  const [quickName, setQuickName] = useState('');

  const all = useMemo(() => tasks ?? [], [tasks]);
  const stats = useMemo(() => {
    const top = all.filter((t) => !t.parent_task_id);
    const open = top.filter((t) => !t.is_completed).length;
    const overdue = top.filter((t) => !t.is_completed && dueState(t.due_date) === 'overdue').length;
    return { total: top.length, open, done: top.length - open, overdue, pct: completionPercent(top.length, open) };
  }, [all]);

  const groups = useMemo<TaskGroup[]>(() => {
    const visible = showCompleted ? all : all.filter((t) => !t.is_completed);
    const childrenOf = new Map<string, TaskWithAssignees[]>();
    for (const t of visible) {
      if (t.parent_task_id) {
        const list = childrenOf.get(t.parent_task_id) ?? [];
        list.push(t);
        childrenOf.set(t.parent_task_id, list);
      }
    }
    const parents = visible.filter((t) => !t.parent_task_id);
    const sectionOrder = [...(sections ?? [])].sort((a, b) => a.position - b.position);
    const result: TaskGroup[] = [];
    const push = (key: string, label: string | null, list: TaskWithAssignees[]) => {
      if (list.length === 0) return;
      result.push({
        key,
        label,
        tasks: list.map((task) => ({ task, subtasks: childrenOf.get(task.id) ?? [] })),
      });
    };
    push('none', null, parents.filter((t) => !t.section_id || !sectionOrder.some((s) => s.id === t.section_id)));
    for (const s of sectionOrder) {
      push(s.id, s.name, parents.filter((t) => t.section_id === s.id));
    }
    return result;
  }, [all, sections, showCompleted]);

  const toggle = async (task: TaskWithAssignees) => {
    try {
      await updateTask.mutateAsync({
        id: task.id,
        projectId: pmProjectId,
        patch: { is_completed: !task.is_completed },
      });
    } catch (err) {
      toast.error(formatError(err, 'Could not update the task. You may need to be a member of the task list.'));
    }
  };

  const quickAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = quickName.trim();
    if (!name) return;
    try {
      await createTask.mutateAsync({ projectId: pmProjectId, name });
      setQuickName('');
    } catch (err) {
      toast.error(formatError(err, 'Could not add the task. You may need to be a member of the task list.'));
    }
  };

  const unlink = async () => {
    if (!window.confirm('Unlink the task list from this engineering project? The task list itself is not deleted.')) return;
    try {
      await updateProject.mutateAsync({ id: project.id, patch: { pm_project_id: null } });
      toast.success('Task list unlinked.');
    } catch (err) {
      toast.error(formatError(err, 'Could not unlink the task list.'));
    }
  };

  const pm = project.pm_project!;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-col gap-4 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Task list</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <Link to={`/projects/${pm.id}`} className="truncate text-base font-medium text-foreground hover:underline">
                {pm.name}
              </Link>
              {pm.archived_at && <span className="text-xs text-muted-foreground">(archived)</span>}
            </div>
            <div className="mt-2 flex items-center gap-3">
              <div className="h-1.5 w-40 overflow-hidden rounded-full bg-secondary" aria-hidden>
                <div className="h-full rounded-full bg-bright-moss-dark transition-all" style={{ width: `${stats.pct}%` }} />
              </div>
              <span className="text-xs text-muted-foreground">
                {stats.done} of {stats.total} done
                {stats.overdue > 0 && (
                  <span className="ml-2 font-medium text-fresh-salmon-dark">{stats.overdue} overdue</span>
                )}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={showCompleted} onCheckedChange={setShowCompleted} ariaLabel="Show completed tasks" />
              Show completed
            </label>
            {canManage && (
              <Button variant="ghost" size="sm" onClick={unlink} disabled={updateProject.isPending}>
                <Unlink className="mr-2 h-4 w-4" />
                Unlink
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to={`/projects/${pm.id}`}>
                Open in Tasks
                <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {canEdit && (
        <form onSubmit={quickAdd} className="flex gap-2">
          <Input
            value={quickName}
            onChange={(e) => setQuickName(e.target.value)}
            placeholder="Add a task…"
            aria-label="New task name"
            maxLength={200}
          />
          <Button type="submit" variant="outline" disabled={!quickName.trim() || createTask.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </form>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={all.length > 0 ? CheckCircle2 : ListTodo}
          title={all.length > 0 ? 'Everything is done' : 'No tasks yet'}
          className="py-10"
        />
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.key}>
              {group.label && (
                <h3 className="mb-1.5 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </h3>
              )}
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                {group.tasks.map(({ task, subtasks }) => (
                  <li key={task.id}>
                    <TaskRow task={task} pmProjectId={pmProjectId} canEdit={canEdit} onToggle={() => toggle(task)} />
                    {subtasks.map((sub) => (
                      <TaskRow
                        key={sub.id}
                        task={sub}
                        pmProjectId={pmProjectId}
                        canEdit={canEdit}
                        onToggle={() => toggle(sub)}
                        nested
                      />
                    ))}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskRow({
  task,
  pmProjectId,
  canEdit,
  onToggle,
  nested = false,
}: {
  task: TaskWithAssignees;
  pmProjectId: string;
  canEdit: boolean;
  onToggle: () => void;
  nested?: boolean;
}) {
  const status = statusOptionMeta(taskStatusOption(task));
  const priority = PRIORITY_META[task.priority];
  const due = dueState(task.due_date);
  const linked = task.project && task.project.id !== pmProjectId;

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2.5',
        nested && 'border-t border-dashed border-border bg-secondary/30 pl-9',
        task.is_completed && 'opacity-60',
      )}
    >
      {nested && <CornerDownRight className="-ml-5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
      <Checkbox
        checked={task.is_completed}
        onCheckedChange={onToggle}
        disabled={!canEdit}
        ariaLabel={task.is_completed ? 'Mark incomplete' : 'Mark complete'}
      />
      <div className="min-w-0 flex-1">
        <Link
          to={`/projects/${pmProjectId}?task=${task.id}`}
          className={cn('block truncate text-sm text-foreground hover:underline', task.is_completed && 'line-through')}
        >
          {task.name}
        </Link>
        {linked && <span className="text-xs text-muted-foreground">from {task.project!.name}</span>}
      </div>
      <div className="hidden items-center gap-2 sm:flex">
        {!task.is_completed && task.priority !== 'medium' && (
          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', priority.className)}>{priority.label}</span>
        )}
        <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', status.className)}>{status.label}</span>
      </div>
      {task.due_date && (
        <span
          className={cn(
            'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
            DUE_TONE[task.is_completed ? 'none' : due],
          )}
        >
          <CalendarDays className="h-3 w-3" />
          {formatDueShort(task.due_date)}
        </span>
      )}
      <AvatarStack people={task.assignees} max={3} className="hidden sm:inline-flex" emptyLabel={null} />
    </div>
  );
}
